"use client"
import { useEffect, useState, useRef, useMemo } from "react" // Added useMemo
import { useDispatch, useSelector } from "react-redux"
import { Box, Text, Skeleton, Tooltip, Badge, Flex, HStack } from "@chakra-ui/react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faFileExcel, faBookmark, faUser } from "@fortawesome/free-solid-svg-icons"
// Removed unused EnvelopeIcon, PhoneIcon, and CalculatorIcon
import { UserIcon, PlusIcon, CalendarDaysIcon, EyeIcon, MapIcon, BuildingOffice2Icon } from "@heroicons/react/24/outline"
import { Link } from "react-router-dom"
import { CSVLink } from "react-csv"

import noData from "./nodata.png"
import { TimeDifference } from "../../common/TimeDifference"
import {
    fetchEmployerSavedCandidateAsync,
    selectEmployerSavedCandidate,
} from "./Candidate/CandidateSlice/EmployerSaveCandidate/EmployerSaveCandidateSlice"

const SavedCandidates = () => {
    const dispatch = useDispatch()
    const savedCandidatesState = useSelector(selectEmployerSavedCandidate)
    const apiResponse = savedCandidatesState?.data // Access the data property from Redux state
    // FIX: Wrapped in useMemo to stabilize the 'candidates' reference
    const candidates = useMemo(() => apiResponse?.data || [], [apiResponse?.data])
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)
    const [csvData, setCsvData] = useState([])
    const csvLinkRef = useRef()

    // CSV Headers
    const csvHeaders = [
        { label: "Name", key: "name" },
        { label: "Email", key: "email" },
        { label: "Phone", key: "phone" },
        { label: "Saved Date", key: "saved_at" },
    ]

    // Pagination states - use values from API response
    const currentPage = apiResponse?.current_page || 1
    const totalCandidates = apiResponse?.total || 0
    // FIX: Removed unused variable 'candidatesPerPage'
    const totalPages = apiResponse?.last_page || 1

    // Pagination handlers
    const handleNextPage = () => {
        if (currentPage < totalPages) {
            dispatch(fetchEmployerSavedCandidateAsync({ page: currentPage + 1 }))
        }
    }

    const handlePrevPage = () => {
        if (currentPage > 1) {
            dispatch(fetchEmployerSavedCandidateAsync({ page: currentPage - 1 }))
        }
    }

    useEffect(() => {
        setIsLoading(true)
        setHasError(false)
        dispatch(fetchEmployerSavedCandidateAsync({ page: 1 })) // Start with page 1
            .unwrap()
            .then(() => setIsLoading(false))
            .catch(() => {
                setHasError(true)
                setIsLoading(false)
            })
        window.scrollTo(0, 0)
    }, [dispatch])

    // Prepare CSV data when candidates data changes
    useEffect(() => {
        if (candidates && candidates.length > 0) {
            const formattedData = candidates.map((item) => ({
                name: item.candidate?.name || "N/A",
                email: item.candidate?.email || "N/A",
                phone: item.candidate?.phone || "Not provided",
                saved_at: item.created_at || "N/A",
            }))
            setCsvData(formattedData)
        }
    }, [candidates])

    const handleViewCandidateProfile = (candidateUuid) => {
        const url = `/employer-dashboard/show-candidate/${candidateUuid}`
        window.open(url, "_blank")
    }

    const handleExportToExcel = () => {
        if (csvLinkRef.current) {
            csvLinkRef.current.link.click()
        }
    }

    const renderCandidateCards = () => {
        if (isLoading) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 px-7 gap-2 w-full">
                    {[...Array(6)].map((_, index) => (
                        <div key={index} className="bg-white rounded-xl shadow-md border border-gray-100 p-3">
                            <div className="space-y-4">
                                <div className="flex items-center gap-3">
                                    <Skeleton height="48px" width="48px" borderRadius="lg" />
                                    <div className="flex-1 space-y-2">
                                        <Skeleton height="20px" width="70%" />
                                        <Skeleton height="16px" width="50%" />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Skeleton height="16px" width="100%" />
                                    <Skeleton height="16px" width="80%" />
                                </div>
                                <Skeleton height="36px" width="100%" />
                            </div>
                        </div>
                    ))}
                </div>
            )
        }

        if (hasError) {
            return (
                <Box textAlign="center" py="10">
                    <Text fontSize="xl" color="red.500">
                        Failed to load saved candidates. Please try again later.
                    </Text>
                </Box>
            )
        }

        if (!candidates || candidates.length === 0) {
            return (
                <div className="flex flex-col items-center justify-center w-full py-10">
                    <img src={noData || "/placeholder.svg"} className="object-contain h-[300px] w-[300px]" alt="No candidates" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">No Saved Candidates Found</h3>
                    <p className="text-gray-600 text-center max-w-md mb-4">
                        You haven't saved any candidate profiles yet. Save candidate profiles to easily access them later.
                    </p>
                    <Link to={`/employer-dashboard/search-candidate`}>
                        <button className="inline-flex items-center px-4 py-2 mb-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-all duration-200">
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Find Candidates
                        </button>
                    </Link>
                </div>
            )
        }

        return (
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 w-full px-7">
                    {candidates.map((item) => (
                        <div
                            key={item.uuid}
                            className="bg-white rounded-xl  border border-gray-100  transition-all duration-300 overflow-hidden"
                        >
                            {/* Header */}
                            <div className="p-3 pb-2">
                                <div className="flex items-start gap-3">
                                    <div className="bg-blue-50 p-3 rounded-lg flex-shrink-0">
                                        <FontAwesomeIcon
                                            icon={faUser}
                                            className="text-blue-500 text-lg"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 leading-tight capitalize">
                                            {item?.candidate_name || "Unknown Candidate"}
                                        </h3>
                                        <div className="flex items-center gap-2">
                                            <Badge colorScheme="purple" size="sm" px={2} py={1} borderRadius="md">
                                                <FontAwesomeIcon icon={faBookmark} className="mr-1" />
                                                Saved
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Body - Single Column Layout */}
                            <div className="px-3 pb-2">
                                <div className="space-y-3">
                                    {/* Contact Information */}
                                    <div className="space-y-2">
                                        {/* City */}
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <MapIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <Tooltip label="City" placement="top" bg="gray.100" color="black" fontSize="sm">
                                                <span className="truncate font-medium">{item?.candidate_city || "Not provided"}</span>
                                            </Tooltip>
                                        </div>
                                        {/* Gender */}
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <UserIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <Tooltip label="Gender" placement="top" bg="gray.100" color="black" fontSize="sm">
                                                <span className="truncate font-medium">{item?.candidate_gender || "Not provided"}</span>
                                            </Tooltip>
                                        </div>
                                        {/* Current Company */}
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <BuildingOffice2Icon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <Tooltip label="Current Company" placement="top" bg="gray.100" color="black" fontSize="sm">
                                                <span className="truncate font-medium">{item?.candidate_current_company || "Not provided"}</span>
                                            </Tooltip>
                                        </div>
                                        {/* Notice Period */}
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                                            <Tooltip label="Notice Period" placement="top" bg="gray.100" color="black" fontSize="sm">
                                                <span className="truncate font-medium">{item?.candidate_notice_period || "Not provided"}</span>
                                            </Tooltip>
                                        </div>
                                    </div>

                                    {/* Saved Date */}
                                    <div className="flex items-center justify-between py-2 bg-gray-50 rounded-lg px-3">
                                        <div className="flex items-center gap-2">
                                            <CalendarDaysIcon className="h-4 w-4 text-purple-500" />
                                            <span className="text-sm text-gray-600">Saved:</span>
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">
                                            <TimeDifference dateTimeString={item.created_at} />
                                        </span>
                                    </div>

                                    {/* Divider */}
                                    <div className="border-t border-gray-100 my-3"></div>

                                    {/* Action Button */}
                                    <button
                                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all duration-200 font-medium"
                                        onClick={() => handleViewCandidateProfile(item?.candidate_uuid)}
                                    >
                                        <EyeIcon className="h-4 w-4" />
                                        View Profile
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Pagination */}
                {totalCandidates > 0 && (
                    <Box mt={8} w="full">
                        <Flex justify="center">
                            <HStack spacing={4} align="center">
                                <button
                                    className={`bg-gray-300 text-black px-4 py-2 rounded font-medium transition-all duration-200 ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-400 active:bg-gray-500"
                                        }`}
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                >
                                    <span className="hidden sm:inline">Previous</span>
                                    <span className="sm:hidden">Prev</span>
                                </button>

                                <Text fontSize="md" fontWeight="medium" color="gray.700" px={4}>
                                    <span className="hidden sm:inline">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <span className="sm:hidden">
                                        {currentPage}/{totalPages}
                                    </span>
                                </Text>

                                <button
                                    className={`bg-blue-500 text-white px-4 py-2 rounded font-medium transition-all duration-200 ${currentPage === totalPages
                                        ? "opacity-50 cursor-not-allowed"
                                        : "hover:bg-blue-600 active:bg-blue-700"
                                        }`}
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </HStack>
                        </Flex>
                    </Box>
                )}
            </>
        )
    }

    return (
        <div className="">
            {/* Hidden CSVLink component */}
            <CSVLink
                data={csvData}
                headers={csvHeaders}
                filename={"saved-candidates.csv"}
                className="hidden"
                ref={csvLinkRef}
            />
            <div className="bg-mainBg p-3">
                <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
                    <div className="flex justify-between flex-wrap">
                        <div>
                            <h1 className="sm:text-4xl text-2xl font-semibold mb-4 text-left">Saved Candidates</h1>
                            <p className="opacity-80 mt-3.5 mb-4 text-black">View all candidate profiles you've saved for later</p>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <Link to="/employer-dashboard/search-candidate">
                                <button className="flex items-center px-2 py-1 sm:px-4 sm:py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200">
                                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                                    Find Candidates
                                </button>
                            </Link>
                        </div>
                    </div>
                    <div className="w-full bg-white rounded-xl">
                        <div className="flex justify-between flex-wrap items-center px-7 py-7 ">
                            <h2 className="text-lg font-semibold">My Saved Candidates</h2>
                            {candidates && candidates.length > 0 && (
                                <button
                                    className="flex items-center px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-md hover:bg-green-100 hover:border-green-300 transition-colors duration-200"
                                    onClick={handleExportToExcel}
                                >
                                    <FontAwesomeIcon icon={faFileExcel} className="mr-2 text-green-600" />
                                    Export to Excel
                                </button>
                            )}
                        </div>
                        {renderCandidateCards()}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SavedCandidates